import { checkCalendar } from './calendar.js';
import type { StoryCtx } from './ctx.js';
import { checkTimelineItem } from './timeline.js';
import { ALLOWED_CHILDREN } from './tree.js';
import type { FieldDef, NodeKind, NodeType, StoryNode, TypeCategory } from './types.js';
/** Gültiger Sortierschlüssel (siehe `rankBetween`): Ziffern und Kleinbuchstaben, nicht auf „0“ endend */
export const RANK = /^[0-9a-z]{0,59}[1-9a-z]$/;

/**
 * Aus der Typ-Kategorie ergibt sich die Art des Nodes auf dem Canvas: eine Szene beginnt als Zeitpunkt (umwandelbar in einen
 * Zeitraum). Foundationals, die Story, Manuskript und Kapitel sind keine Canvas-Nodes.
 */
export function kindForCategory(category: TypeCategory): NodeKind | null {
  switch (category) {
    case 'object':
    case 'place':
      return 'instance';
    case 'normal':
      return 'normal';
    case 'scene':
    case 'event':
      return 'event';
    case 'period':
      return 'period';
    case 'timeline':
      return 'timeline';
    default:
      return null;
  }
}

/** Kategorien, deren Einträge als Bibliothekseinträge (Entities) existieren: intern einmalig, auf dem Canvas Instanzen. */
export const isEntityCategory = (c: TypeCategory): boolean => c === 'object' || c === 'place' || c === 'foundational';

/** Darf ein Node dieser Art unter dem Eltern-Node liegen? Liefert eine Fehlermeldung oder null. */
export function checkPlacement(ctx: StoryCtx, kind: NodeKind, parentId: string): string | null {
  const parent = ctx.nodes.get(parentId);
  if (!parent) return 'Der übergeordnete Eintrag existiert nicht.';
  if (!ALLOWED_CHILDREN[parent.kind].includes(kind)) {
    if (kind === 'timeline') return 'Zeitlinien liegen direkt in der Geschichte.';
    if (parent.kind === 'timeline') return 'Auf einer Zeitlinie liegen nur Szenen (Zeitpunkte und Zeiträume).';
    if (parent.kind === 'event' || parent.kind === 'period') return 'In einer Szene liegen Personen, Orte, Dinge und Notizen – keine weiteren Szenen.';
    return 'Hier kann kein Eintrag dieser Art angelegt werden.';
  }
  return null;
}

/** Ein Typ kann nur gelöscht werden, wenn nichts mehr auf ihn verweist. */
export function typeInUse(ctx: StoryCtx, typeId: string): boolean {
  return ctx.story.nodes.some((n) => n.typeId === typeId) || ctx.story.entities.some((e) => e.typeId === typeId) || ctx.story.groups.some((g) => g.typeId === typeId);
}

/** Gültigkeit eines selbst definierten Typs. Liefert eine Fehlermeldung oder null. */
export function checkTypeDefinition(t: Pick<NodeType, 'name' | 'category' | 'fields' | 'titleKey' | 'hoverKey' | 'imageKey'>): string | null {
  if (!['object', 'place', 'normal'].includes(t.category)) return 'Eigene Typen gehören zur Kategorie Entität, Ort oder Normal.';
  if (t.fields.some((f) => f.type === 'ref')) return 'Verweisfelder gibt es nur in vordefinierten Klassen.';
  const keys = new Set<string>();
  for (const f of t.fields) {
    if (keys.has(f.key)) return `Das Feld „${f.key}“ ist doppelt.`;
    keys.add(f.key);
  }
  const field = (k: string | null) => t.fields.find((f) => f.key === k);
  const title = field(t.titleKey);
  if (!title || title.type === 'image') return 'Wähle ein Textfeld als Titel.';
  if (t.hoverKey && (!field(t.hoverKey) || field(t.hoverKey)!.type === 'image')) return 'Das Hover-Feld muss ein Textfeld sein.';
  if (t.imageKey && field(t.imageKey)?.type !== 'image') return 'Das Titelbild muss ein Bildfeld sein.';
  return null;
}

/** Klassen, deren Felder man frei erweitern darf (auch vordefinierte): Einträge der Bibliothek und Foundationals. */
export const isFieldEditable = (t: Pick<NodeType, 'builtin' | 'category'>): boolean => !t.builtin || t.category === 'object' || t.category === 'place' || t.category === 'foundational';

/**
 * Prüft eine neue Feldliste einer Klasse (Felder hinzufügen, umbenennen, umsortieren, entfernen). Fest bleiben: die Felder für Titel,
 * Hover-Text und Bild, Verweisfelder und die Art jedes vorhandenen Feldes. Liefert eine Fehlermeldung oder null.
 */
export function checkFieldEdit(type: Pick<NodeType, 'titleKey' | 'hoverKey' | 'imageKey' | 'fields'>, next: FieldDef[]): string | null {
  const keys = new Set<string>();
  for (const f of next) {
    if (keys.has(f.key)) return `Das Feld „${f.label}“ ist doppelt.`;
    keys.add(f.key);
    if (f.label.trim() === '') return 'Ein Feld braucht einen Namen.';
    const was = type.fields.find((x) => x.key === f.key);
    if (was && was.type !== f.type) return `Die Art des Feldes „${was.label}“ lässt sich nicht ändern.`;
    if (f.type === 'ref' && (!was || was.refType !== f.refType)) return 'Verweisfelder gibt es nur in vordefinierten Klassen.';
  }
  for (const was of type.fields) {
    const fixed = was.key === type.titleKey || was.key === type.hoverKey || was.key === type.imageKey || was.type === 'ref';
    if (fixed && !keys.has(was.key)) return `Das Feld „${was.label}“ gehört fest zu dieser Klasse.`;
  }
  return null;
}

/** Prüft die Invarianten einer ganzen Story (Tests, Import). Liefert alle gefundenen Verstöße. */
export function checkStory(ctx: StoryCtx): string[] {
  const errors: string[] = [];
  const roots = ctx.story.nodes.filter((n) => n.kind === 'story');
  if (roots.length !== 1 || roots[0]!.id !== ctx.story.rootId) errors.push('Es muss genau eine Geschichte als Wurzel geben.');
  const foundationalTypes = ctx.story.types.filter((t) => t.category === 'foundational');
  for (const t of foundationalTypes) {
    const n = ctx.story.entities.filter((e) => e.typeId === t.id).length;
    if (n !== 1) errors.push(`Das Foundational „${t.name}“ muss genau einmal existieren (gefunden: ${n}).`);
  }
  for (const n of ctx.story.nodes) {
    const type = ctx.types.get(n.typeId);
    if (!type) {
      errors.push(`„${n.id}“ hat einen unbekannten Typ.`);
      continue;
    }
    if (type.category === 'foundational') errors.push(`Grundlagen dürfen nicht auf der Zeichenfläche liegen („${type.name}“).`);
    if (n.kind !== 'story') {
      const err = n.parentId ? checkPlacement(ctx, n.kind, n.parentId) : 'Ohne Elternnode.';
      if (err) errors.push(`${type.name}: ${err}`);
    }
    if (n.kind === 'instance' && !(n.entityId && ctx.entities.has(n.entityId))) errors.push(`${type.name}: Instanz ohne Entität in der Bibliothek.`);
    // angedockte Szenen brauchen ihre Lage auf dem Zeitstrahl, lose Nodes ihre Position auf der Fläche
    if ((n.kind === 'period' || n.kind === 'event') && n.parentId && ctx.nodes.get(n.parentId)?.kind === 'timeline') {
      const err = checkTimelineItem({ kind: n.kind, at: n.at, from: n.from, to: n.to });
      if (err) errors.push(`${type.name}: ${err}`);
    }
    if (n.documents.length > 0 && !type.textual) errors.push(`${type.name}: Diese Klasse hat keine Textdokumente.`);
    if ((n.material?.length ?? 0) > 0 && n.kind !== 'event' && n.kind !== 'period') errors.push(`${type.name}: Material gibt es nur bei Szenen.`);
    if ((n.beatSheets?.length ?? 0) > 0 && n.kind !== 'manuscript') errors.push(`${type.name}: Beat-Listen gibt es nur am Manuskript.`);
    if (n.shift !== undefined && n.shift !== null && n.kind !== 'event' && n.kind !== 'period') errors.push(`${type.name}: Eine Wertverschiebung gibt es nur bei Szenen.`);
    const docErr = checkDocuments(n.documents);
    if (docErr) errors.push(`${type.name}: ${docErr}`);
  }
  for (const c of ctx.story.connections) {
    if (!ctx.nodes.has(c.sourceId) || !ctx.nodes.has(c.targetId)) errors.push('Verbindung mit fehlendem Eintrag.');
  }
  errors.push(...checkNarrative(ctx));
  return errors;
}

/** Regeln der Erzählebene: genau ein Manuskript, Kapitel der Szenen, Sortierschlüssel, Verweisfelder. Abschnitte und Kapitel im Manuskript teilen sich die Sortierschlüssel. */
function checkNarrative(ctx: StoryCtx): string[] {
  const errors: string[] = [];
  const manuscripts = ctx.story.nodes.filter((n) => n.kind === 'manuscript');
  if (manuscripts.length !== 1) errors.push(`Es muss genau ein Manuskript geben (gefunden: ${manuscripts.length}).`);
  const ranks = new Map<string, Set<string>>();
  const rankIn = (group: string, n: StoryNode, typeName: string): void => {
    if (!n.rank || !RANK.test(n.rank)) return void errors.push(`${typeName}: ungültiger Sortierschlüssel „${n.rank ?? ''}“.`);
    const taken = ranks.get(group) ?? new Set<string>();
    if (taken.has(n.rank)) errors.push(`${typeName}: Der Sortierschlüssel „${n.rank}“ kommt doppelt vor.`);
    taken.add(n.rank);
    ranks.set(group, taken);
  };
  for (const n of ctx.story.nodes) {
    const type = ctx.types.get(n.typeId);
    if (!type) continue;
    const scene = n.kind === 'event' || n.kind === 'period';
    if (n.kind === 'scene') errors.push(`${type.name}: veraltete Szene (wird beim Laden übernommen).`);
    if (n.kind === 'chapter' || n.kind === 'section') rankIn(`chapters:${n.parentId ?? ''}`, n, type.name);
    if (n.chapterId) {
      if (!scene) errors.push(`${type.name}: Nur Szenen stehen in einem Kapitel.`);
      else if (ctx.nodes.get(n.chapterId)?.kind !== 'chapter') errors.push(`${type.name}: Das Kapitel der Szene gibt es nicht.`);
      else rankIn(`scenes:${n.chapterId}`, n, type.name);
    }
    if ((n.narrates ?? []).length > 0) errors.push(`${type.name}: Erzählt-Listen gibt es nicht mehr (Szene und Ereignis sind eins).`);
    if (n.narrateSkip && !scene) errors.push(`${type.name}: „Bewusst nicht erzählt“ gibt es nur bei Szenen.`);
    if (n.calendar) {
      if (n.id !== ctx.story.rootId) errors.push(`${type.name}: Einen Kalender hat nur die Geschichte.`);
      const err = checkCalendar(n.calendar);
      if (err) errors.push(`Kalender: ${err}`);
    }
    for (const def of type.fields) {
      if (def.type !== 'ref') continue;
      const err = checkRefField(ctx, def.refType, n.fields[def.key] ?? null);
      if (err) errors.push(`${type.name}: „${def.label}“ ${err}`);
    }
  }
  return errors;
}

/** Prüft den Wert eines Verweisfelds (`ref`): leer oder ein Eintrag der verlangten Klasse. Liefert eine Fehlermeldung oder null. */
export function checkRefField(ctx: StoryCtx, refType: string | undefined, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const entity = typeof value === 'string' ? ctx.entities.get(value) : undefined;
  if (!entity) return 'verweist auf keine vorhandene Entität.';
  if (!refAccepts(ctx, refType, entity.typeId)) return `erwartet eine Entität der Klasse „${refType}“.`;
  return null;
}

/**
 * Passt ein Element der Klasse `typeId` in ein Verweisfeld für die Klasse `refType`? Gleicher Name; ein Feld für die vordefinierte Klasse
 * Ort (Schauplatz) nimmt jeden Ort, auch aus eigenen Ortsklassen.
 */
export function refAccepts(ctx: Pick<StoryCtx, 'types'>, refType: string | undefined, typeId: string): boolean {
  const t = ctx.types.get(typeId);
  if (!t) return false;
  if (!refType || t.name === refType) return true;
  return t.category === 'place' && [...ctx.types.values()].some((x) => x.builtin && x.category === 'place' && x.name === refType);
}

/** Entities ohne verbliebene Instanzen sind erlaubt (Bibliothek). Hilfe für „Wo kommt der Eintrag vor?“ */
export function instancesOf(ctx: StoryCtx, entityId: string): StoryNode[] {
  return ctx.story.nodes.filter((n) => n.entityId === entityId);
}


export const MAX_DOCUMENTS = 40;
export const MAX_DOCUMENT_NAME = 80;
export const MAX_DOCUMENT_TEXT = 500_000;

/**
 * Prüfsumme eines Textes (FNV-1a über die UTF-16-Zeichen, dazu die Länge): Der Client schickt beim Speichern die des Stands mit, auf
 * dem er aufbaut; weicht der Text auf dem Server davon ab, hat ihn inzwischen jemand anders geändert (anderes Fenster oder Gerät).
 */
export function textHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
  return `${(h >>> 0).toString(36)}.${text.length.toString(36)}`;
}

/** Prüft die Textdokumente eines Nodes (eindeutige IDs und Namen, Längen). Liefert eine Fehlermeldung oder null. */
export function checkDocuments(docs: { id: string; name: string; text: string }[]): string | null {
  if (docs.length > MAX_DOCUMENTS) return `Höchstens ${MAX_DOCUMENTS} Textdokumente je Eintrag.`;
  const ids = new Set<string>();
  const names = new Set<string>();
  for (const d of docs) {
    if (ids.has(d.id)) return 'Textdokumente brauchen eindeutige IDs.';
    ids.add(d.id);
    const name = d.name.trim();
    if (name === '') return 'Ein Textdokument braucht einen Namen.';
    if (name.length > MAX_DOCUMENT_NAME) return `Der Name eines Textdokuments darf höchstens ${MAX_DOCUMENT_NAME} Zeichen haben.`;
    if (names.has(name.toLowerCase())) return `Ein Textdokument „${name}“ gibt es schon.`;
    names.add(name.toLowerCase());
    if (d.text.length > MAX_DOCUMENT_TEXT) return `Ein Textdokument darf höchstens ${MAX_DOCUMENT_TEXT} Zeichen haben.`;
  }
  return null;
}

/**
 * Darf der Node in einen anderen Node verschoben werden? Liefert eine Fehlermeldung oder null.
 * Bewusst großzügig: Jede Instanz, jeder normale Node und jede Szene darf in jeden Node, der Kinder dieser Art aufnimmt, auch an
 * einem Zeitstrahl andocken (Szenen) oder unter das Manuskript (Szene von der Fläche nehmen). Verboten ist nur, was das Modell zerstören würde (Zeitstrahle und die Geschichte
 * bleiben, wo sie sind, kein Verschieben in sich selbst oder in den eigenen Inhalt). Weitere Einschränkungen gehören später hierher.
 */
export function checkMove(ctx: StoryCtx, nodeId: string, targetId: string): string | null {
  const node = ctx.nodes.get(nodeId);
  const target = ctx.nodes.get(targetId);
  if (!node || !target) return 'Der Eintrag existiert nicht.';
  if (node.kind === 'story' || node.kind === 'timeline') return 'Die Geschichte und Zeitlinien lassen sich nicht in andere Einträge schieben.';
  if (node.kind === 'manuscript') return 'Das Manuskript lässt sich nicht verschieben.';
  if (target.id === node.id) return 'Ein Eintrag lässt sich nicht in sich selbst schieben.';
  if (node.parentId === target.id) return 'Der Eintrag liegt schon darin.';
  for (let cur = target.parentId ? ctx.nodes.get(target.parentId) : undefined; cur; cur = cur.parentId ? ctx.nodes.get(cur.parentId) : undefined) {
    if (cur.id === node.id) return 'Ein Eintrag lässt sich nicht in seinen eigenen Inhalt schieben.';
  }
  return checkPlacement(ctx, node.kind, target.id);
}
